import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import WarningIcon from '@mui/icons-material/Warning';
import { useQuery } from '@tanstack/react-query';
import { Address } from '../../common/model/dto/address';
import { OrderItem } from '../../common/model/dto/order/order-item';
import { OrderPaymentStatus } from '../../common/model/type/order-payment-status.enum';
import { OrderStatus } from '../../common/model/type/order-status.enum';
import { Resource } from '../../common/model/type/resource.enum';
import breakpoints from '../../design-system/breakpoints';
import Colours from '../../design-system/colours';
import { useAdminOrderMutations } from '../../hooks/use-admin-order-mutations';
import useAuthorized from '../../hooks/use-authorized';
import OrdersService from '../../services/orders.service';
import OvBackButton from '../UI/atoms/OvBackButton';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvConfirmationDialog from '../UI/molecules/OvConfirmationDialog';
import OvEditOrderDialog from '../UI/molecules/OvEditOrderDialog';
import OvEditOrderPaymentStatusDialog from '../UI/molecules/OvEditOrderPaymentStatusDialog';
import OvEditOrderStatusDialog from '../UI/molecules/OvEditOrderStatusDialog';
import OvEditShippingAddressDialog from '../UI/molecules/OvEditShippingAddressDialog';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvConversionSubmissionDetails from '../UI/organisms/OvConversionSubmissionDetails';
import OvOrderActivityList from '../UI/organisms/OvOrderActivityList';
import OvOrderCustomerSection from '../UI/organisms/OvOrderCustomerSection';
import OvOrderDeliverySection from '../UI/organisms/OvOrderDeliverySection';
import OvOrderDetailsHeader from '../UI/organisms/OvOrderDetailsHeader';
import OvOrderInfoSection from '../UI/organisms/OvOrderInfoSection';
import OvOrderItemsSection from '../UI/organisms/OvOrderItemsSection';
import OvOrderMarketingSection from '../UI/organisms/OvOrderMarketingSection';

const OrderDetails: FC = () => {
  const { t } = useTranslation();
  const { orderId = '' } = useParams<{ orderId: string }>();

  const applicationOperations = useAuthorized(Resource.Application);
  const orderOperations = useAuthorized(Resource.Order);
  const orderActivityOperations = useAuthorized(Resource.OrderActivity);

  const orderUpdateAllowed = !!(
    orderOperations.update || applicationOperations.supervise
  );
  const orderActivitiesReadAllowed = !!(
    orderActivityOperations.read || applicationOperations.supervise
  );

  const [isEditOrderOpen, setIsEditOrderOpen] = useState<boolean>(false);
  const [isEditAddressOpen, setIsEditAddressOpen] = useState<boolean>(false);
  const [isEditStatusOpen, setIsEditStatusOpen] = useState<boolean>(false);
  const [isEditPaymentStatusOpen, setIsEditPaymentStatusOpen] =
    useState<boolean>(false);
  const [isSubmitConfirmationOpen, setIsSubmitConfirmationOpen] =
    useState<boolean>(false);

  const {
    data: order,
    isFetching: isGetOrderFetching,
    error: getOrderError,
    refetch: refetchOrder,
  } = useQuery({
    queryKey: ['admin-order', orderId],
    queryFn: () => OrdersService.getBackendOrderById(orderId),
    retry: 3,
    enabled: !!orderId,
  });

  const {
    data: paginatedOrderActivities,
    isFetching: isGetOrderActivitiesFetching,
    error: getOrderActivitiesError,
  } = useQuery({
    queryKey: ['admin-order-activities', orderId],
    queryFn: () => OrdersService.getOrderActivitesById(order?.internal_id),
    retry: 3,
    enabled: !!order?.internal_id && orderActivitiesReadAllowed,
  });

  const orderActivities = paginatedOrderActivities?.docs ?? [];

  const { updateOrder, submitOrder } = useAdminOrderMutations(orderId);

  const onSaveEditedOrder = (updates: { discount_code?: string }) => {
    setIsEditOrderOpen(false);
    updateOrder.mutate(updates);
  };

  const onSaveEditedAddress = (shipping_address: Address) => {
    setIsEditAddressOpen(false);
    updateOrder.mutate({ shipping_address });
  };

  const onSaveStatusChange = ({ status }: { status?: OrderStatus }) => {
    if (status) {
      setIsEditStatusOpen(false);
      updateOrder.mutate({ status });
    }
  };

  const onSavePaymentStatusChange = ({
    payment_status,
  }: {
    payment_status?: OrderPaymentStatus;
  }) => {
    if (payment_status) {
      setIsEditPaymentStatusOpen(false);
      updateOrder.mutate({ payment_status });
    }
  };

  const onConfirmSubmit = () => {
    setIsSubmitConfirmationOpen(false);
    submitOrder.mutate();
  };

  const onItemsChange = (items: OrderItem[]) => {
    updateOrder.mutate({ items });
  };

  const isLoading =
    isGetOrderFetching ||
    isGetOrderActivitiesFetching ||
    updateOrder.isPending ||
    submitOrder.isPending;

  if (!order) {
    return (
      <DetailPageWrapper>
        {getOrderError ? (
          <OvRecordDetailsCanvas>
            <OvListStateMessage
              isAlert
              icon={<WarningIcon />}
              title={t('orders.states.detailsErrorTitle')}
              description={getOrderError.message}
            >
              <StateActions>
                <OvBackButton label={t('app.subPages.menu.orders')} />
                <OvCompactButton onClick={() => refetchOrder()}>
                  {t('common.actions.retry')}
                </OvCompactButton>
              </StateActions>
            </OvListStateMessage>
          </OvRecordDetailsCanvas>
        ) : (
          <OvLoadingIndicator position={'fixed'} />
        )}
      </DetailPageWrapper>
    );
  }

  const errors = [
    getOrderError,
    getOrderActivitiesError,
    updateOrder.error,
    submitOrder.error,
  ].filter((error): error is Error => !!error);

  return (
    <DetailPageWrapper aria-busy={isLoading}>
      {errors.map((error) => (
        <OvErrorMessage key={error.message} message={error.message} />
      ))}

      <OvRecordDetailsCanvas>
        <OvOrderDetailsHeader
          order={order}
          isUpdateAllowed={orderUpdateAllowed}
          isActionDisabled={isLoading}
          onEditStatus={() => setIsEditStatusOpen(true)}
          onEditPaymentStatus={() => setIsEditPaymentStatusOpen(true)}
          onSubmit={() => setIsSubmitConfirmationOpen(true)}
        />
        <Body>
          <ProductsArea>
            <OvOrderItemsSection
              order={order}
              isUpdateAllowed={orderUpdateAllowed}
              onItemsChange={onItemsChange}
            />
          </ProductsArea>
          <CustomerArea>
            <OvOrderCustomerSection
              order={order}
              isUpdateAllowed={orderUpdateAllowed}
              isEditDisabled={isLoading}
              onEditShippingAddress={() => setIsEditAddressOpen(true)}
            />
          </CustomerArea>
          <DeliveryArea>
            <OvOrderDeliverySection order={order} />
          </DeliveryArea>
          <InfoArea>
            <OvOrderInfoSection
              order={order}
              isUpdateAllowed={orderUpdateAllowed}
              isEditDisabled={isLoading}
              onEdit={() => setIsEditOrderOpen(true)}
            />
          </InfoArea>
        </Body>
        <OvOrderMarketingSection key={order.id} order={order} />
      </OvRecordDetailsCanvas>

      {order.conversion_submission_event && (
        <OvConversionSubmissionDetails
          conversionSubmissionEvent={order.conversion_submission_event}
        />
      )}

      {orderActivities.length > 0 && (
        <OvOrderActivityList orderActivities={orderActivities} />
      )}

      <OvEditOrderDialog
        isOpen={isEditOrderOpen}
        onCancel={() => setIsEditOrderOpen(false)}
        onSave={onSaveEditedOrder}
        title={`${t('common.actions.editOrder')} - ${order.order_number}`}
        order={order}
      />

      <OvEditShippingAddressDialog
        isOpen={isEditAddressOpen}
        onCancel={() => setIsEditAddressOpen(false)}
        onSave={onSaveEditedAddress}
        title={`${t('common.actions.editAddress')} - ${order.order_number}`}
        address={order.shipping_address}
      />

      <OvEditOrderStatusDialog
        isOpen={isEditStatusOpen}
        onCancel={() => setIsEditStatusOpen(false)}
        onSave={onSaveStatusChange}
        currentStatus={order.status}
      />

      <OvEditOrderPaymentStatusDialog
        isOpen={isEditPaymentStatusOpen}
        onCancel={() => setIsEditPaymentStatusOpen(false)}
        onSave={onSavePaymentStatusChange}
        currentPaymentStatus={order.payment_status}
      />

      <OvConfirmationDialog
        isOpen={isSubmitConfirmationOpen}
        onCancel={() => setIsSubmitConfirmationOpen(false)}
        onConfirm={onConfirmSubmit}
        description={'orders.confirmProceedToShipping'}
        descriptionVariable={{
          dropshippingPartner: order.dropshipping_partner,
        }}
        title={t('common.areYouSure')}
      />

      {isLoading && <OvLoadingIndicator position={'fixed'} />}
    </DetailPageWrapper>
  );
};

export default OrderDetails;

const DetailPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.375rem;
  margin-left: 1rem;
  min-width: 0;
  color: ${Colours.OV_BASE};
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(18.75rem, 1fr);
  grid-template-areas:
    'products customer'
    'delivery info';

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'customer'
      'products'
      'delivery'
      'info';
  }
`;

const ProductsArea = styled.div`
  grid-area: products;
  min-width: 0;

  @media (max-width: ${breakpoints.md}) {
    border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  }
`;

const CustomerArea = styled.div`
  grid-area: customer;
  min-width: 0;
  border-left: 1px solid ${Colours.OV_BORDER_COLOR_V3};

  @media (max-width: ${breakpoints.md}) {
    border-left: 0;
  }
`;

const DeliveryArea = styled.div`
  grid-area: delivery;
  min-width: 0;
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
`;

const InfoArea = styled.div`
  grid-area: info;
  min-width: 0;
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  border-left: 1px solid ${Colours.OV_BORDER_COLOR_V3};

  @media (max-width: ${breakpoints.md}) {
    border-left: 0;
  }
`;

const StateActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;
